import React, {useState, useMemo} from 'react';
import {motion, AnimatePresence} from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../../common/SafeIcon';
import {useAuth0Data} from '../../../context/Auth0DataContext';
import {format, parseISO, isPast, isToday} from 'date-fns';
import CreateProjectModal from '../../../components/ProjectManagement/CreateProjectModal';

const {FiCheckSquare, FiSquare, FiClock, FiAlertTriangle, FiUser, FiFilter, FiPlus, FiEdit, FiTrash2, FiSave, FiX, FiTarget, FiSearch} = FiIcons;

const TasksView = () => {
  const {data, updateItem, getAllUsers, user} = useAuth0Data();
  const [filter, setFilter] = useState('all');
  const [selectedProject, setSelectedProject] = useState('all');
  const [selectedAssignee, setSelectedAssignee] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState('dueDate');
  const [sortOrder, setSortOrder] = useState('asc');
  const [editingTask, setEditingTask] = useState(null);
  const [showTaskForm, setShowTaskForm] = useState(false);
  const [showCreateProjectModal, setShowCreateProjectModal] = useState(false);
  const [taskFormData, setTaskFormData] = useState({
    title: '',
    assignee: '',
    dueDate: '',
    status: 'todo',
    priority: 'medium',
    projectId: '',
    notes: ''
  });

  const allUsers = useMemo(() => getAllUsers(), [getAllUsers]);

  const allTasks = useMemo(() => {
    return data.projects.flatMap(project =>
      (project.tasks || []).map(task => ({
        ...task,
        projectTitle: project.title,
        projectId: project.id
      }))
    );
  }, [data.projects]);

  const filteredTasks = useMemo(() => {
    let tasks = allTasks;

    // Filter by project
    if (selectedProject !== 'all') {
      tasks = tasks.filter(task => task.projectId === selectedProject);
    }

    // Filter by assignee
    if (selectedAssignee !== 'all') {
      tasks = tasks.filter(task => task.assignee === selectedAssignee);
    }

    // Filter by status
    switch (filter) {
      case 'pending':
        tasks = tasks.filter(task => task.status === 'todo' || task.status === 'in-progress');
        break;
      case 'overdue':
        tasks = tasks.filter(task =>
          (task.status === 'todo' || task.status === 'in-progress') &&
          task.dueDate &&
          isPast(parseISO(task.dueDate)) &&
          !isToday(parseISO(task.dueDate))
        );
        break;
      case 'completed':
        tasks = tasks.filter(task => task.status === 'completed');
        break;
      case 'today':
        tasks = tasks.filter(task =>
          task.dueDate && isToday(parseISO(task.dueDate))
        );
        break;
      default:
        // 'all' - no additional filtering
        break;
    }

    // Filter by search term
    if (searchTerm.trim()) {
      const search = searchTerm.toLowerCase();
      tasks = tasks.filter(task =>
        task.title.toLowerCase().includes(search) ||
        task.assignee.toLowerCase().includes(search) ||
        task.projectTitle.toLowerCase().includes(search) ||
        (task.dueDate && format(parseISO(task.dueDate), 'MMM dd, yyyy').toLowerCase().includes(search))
      );
    }

    // Sort tasks
    return [...tasks].sort((a, b) => {
      let aValue, bValue;

      switch (sortBy) {
        case 'title':
          aValue = a.title.toLowerCase();
          bValue = b.title.toLowerCase();
          break;
        case 'assignee':
          aValue = a.assignee.toLowerCase();
          bValue = b.assignee.toLowerCase();
          break;
        case 'project':
          aValue = a.projectTitle.toLowerCase();
          bValue = b.projectTitle.toLowerCase();
          break;
        case 'priority':
          const priorityOrder = {'high': 3, 'medium': 2, 'low': 1};
          aValue = priorityOrder[a.priority] || 0;
          bValue = priorityOrder[b.priority] || 0;
          break;
        case 'status':
          aValue = a.status.toLowerCase();
          bValue = b.status.toLowerCase();
          break;
        case 'dueDate':
        default:
          if (!a.dueDate && !b.dueDate) return 0;
          if (!a.dueDate) return 1;
          if (!b.dueDate) return -1;
          aValue = new Date(a.dueDate);
          bValue = new Date(b.dueDate);
          break;
      }

      if (sortBy === 'dueDate' || sortBy === 'priority') {
        return sortOrder === 'asc' ? aValue - bValue : bValue - aValue;
      } else {
        if (aValue < bValue) return sortOrder === 'asc' ? -1 : 1;
        if (aValue > bValue) return sortOrder === 'asc' ? 1 : -1;
        return 0;
      }
    });
  }, [allTasks, selectedProject, selectedAssignee, filter, searchTerm, sortBy, sortOrder]);

  const handleSort = (column) => {
    if (sortBy === column) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(column);
      setSortOrder('asc');
    }
  };

  const handleTaskToggle = (task) => {
    const newStatus = task.status === 'completed' ? 'todo' : 'completed';
    const project = data.projects.find(p => p.id === task.projectId);
    if (project) {
      const updatedTasks = (project.tasks || []).map(t => 
        t.id === task.id ? {...t, status: newStatus} : t
      );
      updateItem('projects', project.id, {...project, tasks: updatedTasks});
    }
  };

  const handleEditTask = (task) => {
    setEditingTask(task.id);
    setTaskFormData({
      title: task.title,
      assignee: task.assignee,
      dueDate: task.dueDate,
      status: task.status,
      priority: task.priority || 'medium',
      projectId: task.projectId,
      notes: task.notes || ''
    });
  };

  const handleSaveTask = () => {
    if (editingTask && taskFormData.title.trim() && taskFormData.assignee.trim()) {
      // Update existing task
      const project = data.projects.find(p => 
        (p.tasks || []).some(t => t.id === editingTask)
      );
      if (project) {
        const updatedTasks = (project.tasks || []).map(task =>
          task.id === editingTask ? {
            ...task,
            title: taskFormData.title,
            assignee: taskFormData.assignee,
            dueDate: taskFormData.dueDate,
            status: taskFormData.status,
            priority: taskFormData.priority,
            notes: taskFormData.notes
          } : task
        );
        updateItem('projects', project.id, {...project, tasks: updatedTasks});
      }
      setEditingTask(null);
      resetForm();
    }
  };

  const handleAddTask = () => {
    if (taskFormData.title.trim() && taskFormData.assignee.trim() && taskFormData.projectId) {
      const project = data.projects.find(p => p.id === taskFormData.projectId);
      if (project) {
        const newTask = {
          id: Date.now(),
          title: taskFormData.title,
          assignee: taskFormData.assignee,
          dueDate: taskFormData.dueDate,
          status: taskFormData.status,
          priority: taskFormData.priority,
          notes: taskFormData.notes
        };
        
        // Ensure tasks array exists and add new task
        const updatedTasks = [...(project.tasks || []), newTask];
        updateItem('projects', project.id, {...project, tasks: updatedTasks});
        
        // Close form and reset
        setShowTaskForm(false);
        resetForm();
        console.log('Task added successfully!');
      }
    } else {
      alert('Please fill in all required fields (Title, Assignee, and Project)');
    }
  };

  const handleDeleteTask = (task) => {
    if (confirm('Are you sure you want to delete this task?')) {
      const project = data.projects.find(p => p.id === task.projectId);
      if (project) {
        const updatedTasks = (project.tasks || []).filter(t => t.id !== task.id);
        updateItem('projects', project.id, {...project, tasks: updatedTasks});
      }
    }
  };

  const resetForm = () => {
    setTaskFormData({
      title: '',
      assignee: '',
      dueDate: '',
      status: 'todo',
      priority: 'medium',
      projectId: '',
      notes: ''
    });
  };

  const handleCreateProjectSuccess = () => {
    console.log('Project created successfully!');
  };

  const getTaskIcon = (task) => {
    if (task.status === 'completed') return FiCheckSquare;
    if (task.dueDate && isPast(parseISO(task.dueDate)) && !isToday(parseISO(task.dueDate))) {
      return FiAlertTriangle;
    }
    return FiSquare;
  };

  const getTaskColor = (task) => {
    if (task.status === 'completed') return 'text-green-600';
    if (task.dueDate && isPast(parseISO(task.dueDate)) && !isToday(parseISO(task.dueDate))) {
      return 'text-red-600';
    }
    if (task.dueDate && isToday(parseISO(task.dueDate))) return 'text-yellow-600';
    return 'text-gray-600';
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'high': return 'bg-red-100 text-red-800';
      case 'medium': return 'bg-yellow-100 text-yellow-800';
      case 'low': return 'bg-green-100 text-green-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'todo': return 'bg-blue-100 text-blue-800';
      case 'in-progress': return 'bg-yellow-100 text-yellow-800';
      case 'completed': return 'bg-green-100 text-green-800';
      case 'review': return 'bg-purple-100 text-purple-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  // Get task counts - if My Tasks is active, show personal counts
  const isMyTasksActive = selectedAssignee === user?.name;
  const tasksForStats = useMemo(() => {
    return isMyTasksActive
      ? allTasks.filter(task => task.assignee === user?.name)
      : allTasks;
  }, [isMyTasksActive, allTasks, user?.name]);

  const { pendingTasks, overdueTasks, completedTasks, todayTasks } = useMemo(() => ({
    pendingTasks: tasksForStats.filter(task => task.status === 'todo' || task.status === 'in-progress').length,
    overdueTasks: tasksForStats.filter(task =>
      (task.status === 'todo' || task.status === 'in-progress') &&
      task.dueDate &&
      isPast(parseISO(task.dueDate)) &&
      !isToday(parseISO(task.dueDate))
    ).length,
    completedTasks: tasksForStats.filter(task => task.status === 'completed').length,
    todayTasks: tasksForStats.filter(task =>
      task.dueDate && isToday(parseISO(task.dueDate))
    ).length
  }), [tasksForStats]);

  return (
    <>
      <motion.div
        initial={{opacity: 0, y: 20}}
        animate={{opacity: 1, y: 0}}
        className="bg-white rounded-xl shadow-soft border border-gray-100 p-6"
      >
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center">
            <div className="w-10 h-10 bg-indigo-100 rounded-lg flex items-center justify-center">
              <SafeIcon icon={FiCheckSquare} className="w-5 h-5 text-indigo-600" />
            </div>
            <h3 className="ml-3 text-xl font-bold text-gray-900">Task Management</h3>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setShowTaskForm(true)}
              className="flex items-center px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
            >
              <SafeIcon icon={FiPlus} className="w-4 h-4 mr-2" />
              Add Task
            </button>
            <button
              onClick={() => setShowCreateProjectModal(true)}
              className="flex items-center px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
            >
              <SafeIcon icon={FiPlus} className="w-4 h-4 mr-2" />
              Create Project
            </button>
          </div>
        </div>

        {/* Task Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <div className="text-center p-3 bg-blue-50 rounded-lg">
            <p className="text-xl font-bold text-blue-600">{pendingTasks}</p>
            <p className="text-xs text-gray-600">Pending</p>
          </div>
          <div className="text-center p-3 bg-yellow-50 rounded-lg">
            <p className="text-xl font-bold text-yellow-600">{todayTasks}</p>
            <p className="text-xs text-gray-600">Due Today</p>
          </div>
          <div className="text-center p-3 bg-red-50 rounded-lg">
            <p className="text-xl font-bold text-red-600">{overdueTasks}</p>
            <p className="text-xs text-gray-600">Overdue</p>
          </div>
          <div className="text-center p-3 bg-green-50 rounded-lg">
            <p className="text-xl font-bold text-green-600">{completedTasks}</p>
            <p className="text-xs text-gray-600">Completed</p>
          </div>
        </div>

        {/* Filters and Sort */}
        <div className="flex flex-col lg:flex-row gap-4 mb-6">
          <div className="flex items-center gap-2 flex-1">
            <SafeIcon icon={FiSearch} className="w-4 h-4 text-gray-500" />
            <div className="relative flex-1">
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search tasks, assignees, projects, or dates..."
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              />
              {searchTerm && (
                <button
                  onClick={() => setSearchTerm('')}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  <SafeIcon icon={FiX} className="w-4 h-4" />
                </button>
              )}
            </div>
          </div>
          
          <div className="flex flex-wrap gap-2">
            {/* My Tasks Toggle */}
            <button
              onClick={() => setSelectedAssignee(selectedAssignee === user?.name ? 'all' : user?.name || 'all')}
              className={`flex items-center px-3 py-2 rounded-lg border transition-colors ${
                selectedAssignee === user?.name
                  ? 'bg-primary-600 text-white border-primary-600'
                  : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
              }`}
            >
              <SafeIcon icon={FiUser} className="w-4 h-4 mr-2" />
              My Tasks
            </button>

            <div className="flex items-center gap-2">
              <SafeIcon icon={FiFilter} className="w-4 h-4 text-gray-500" />
              <select
                value={filter}
                onChange={(e) => setFilter(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              >
                <option value="all">All Tasks</option>
                <option value="pending">Pending</option>
                <option value="today">Due Today</option>
                <option value="overdue">Overdue</option>
                <option value="completed">Completed</option>
              </select>
            </div>

            <div className="flex items-center gap-2">
              <SafeIcon icon={FiTarget} className="w-4 h-4 text-gray-500" />
              <select
                value={selectedProject}
                onChange={(e) => setSelectedProject(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              >
                <option value="all">All Projects</option>
                {[...data.projects].sort((a, b) => a.title.localeCompare(b.title)).map(project => (
                  <option key={project.id} value={project.id}>{project.title}</option>
                ))}
              </select>
            </div>

            <div className="flex items-center gap-2">
              <SafeIcon icon={FiUser} className="w-4 h-4 text-gray-500" />
              <select
                value={selectedAssignee}
                onChange={(e) => setSelectedAssignee(e.target.value)}
                className={`px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 ${
                  selectedAssignee !== 'all' ? 'border-primary-500 bg-primary-50' : 'border-gray-300'
                }`}
              >
                <option value="all">All Assignees</option>
                {user?.name && <option value={user.name}>My Tasks ({user.name})</option>}
                <optgroup label="Team Members">
                  {allUsers.filter(u => u.name !== user?.name).map(u => (
                    <option key={u.id} value={u.name}>{u.name}</option>
                  ))}
                </optgroup>
              </select>
            </div>
          </div>
        </div>

        {/* Add Task Form */}
        {showTaskForm && (
          <motion.div
            initial={{opacity: 0, height: 0}}
            animate={{opacity: 1, height: 'auto'}}
            className="mb-6 p-4 bg-gray-50 rounded-lg"
          >
            <h4 className="font-semibold text-gray-900 mb-4">Add New Task</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">Task Title *</label>
                <input
                  type="text"
                  value={taskFormData.title}
                  onChange={(e) => setTaskFormData(prev => ({...prev, title: e.target.value}))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  placeholder="Enter task title"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Assignee *</label>
                <select
                  value={taskFormData.assignee}
                  onChange={(e) => setTaskFormData(prev => ({...prev, assignee: e.target.value}))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  required
                >
                  <option value="">Select Assignee</option>
                  {allUsers.map(user => (
                    <option key={user.id} value={user.name}>{user.name}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Due Date</label>
                <input
                  type="date"
                  value={taskFormData.dueDate}
                  onChange={(e) => setTaskFormData(prev => ({...prev, dueDate: e.target.value}))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Project *</label>
                <select
                  value={taskFormData.projectId}
                  onChange={(e) => setTaskFormData(prev => ({...prev, projectId: e.target.value}))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  required
                >
                  <option value="">Select Project</option>
                  {data.projects.map(project => (
                    <option key={project.id} value={project.id}>{project.title}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Priority</label>
                <select
                  value={taskFormData.priority}
                  onChange={(e) => setTaskFormData(prev => ({...prev, priority: e.target.value}))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                >
                  <option value="low">Low Priority</option>
                  <option value="medium">Medium Priority</option>
                  <option value="high">High Priority</option>
                </select>
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
                <textarea
                  value={taskFormData.notes}
                  onChange={(e) => setTaskFormData(prev => ({...prev, notes: e.target.value}))}
                  rows={2}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  placeholder="Additional notes or context for this task"
                />
              </div>
            </div>
            <div className="flex gap-2 mt-4">
              <button
                onClick={handleAddTask}
                disabled={!taskFormData.title.trim() || !taskFormData.assignee.trim() || !taskFormData.projectId}
                className="flex items-center px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed"
              >
                <SafeIcon icon={FiSave} className="w-4 h-4 mr-2" />
                Add Task
              </button>
              <button
                onClick={() => {setShowTaskForm(false); resetForm();}}
                className="flex items-center px-4 py-2 bg-gray-300 text-gray-700 rounded-lg hover:bg-gray-400 transition-colors"
              >
                <SafeIcon icon={FiX} className="w-4 h-4 mr-2" />
                Cancel
              </button>
            </div>
          </motion.div>
        )}

        {/* Task Table */}
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-gray-200 text-left">
                <th className="pb-3 text-xs font-medium text-gray-500 uppercase tracking-wide">Status</th>
                <th 
                  className="pb-3 text-xs font-medium text-gray-500 uppercase tracking-wide cursor-pointer hover:text-gray-700"
                  onClick={() => handleSort('title')}
                >
                  Task {sortBy === 'title' && (sortOrder === 'asc' ? '↑' : '↓')}
                </th>
                <th 
                  className="pb-3 text-xs font-medium text-gray-500 uppercase tracking-wide cursor-pointer hover:text-gray-700"
                  onClick={() => handleSort('assignee')}
                >
                  Assignee {sortBy === 'assignee' && (sortOrder === 'asc' ? '↑' : '↓')}
                </th>
                <th 
                  className="pb-3 text-xs font-medium text-gray-500 uppercase tracking-wide cursor-pointer hover:text-gray-700"
                  onClick={() => handleSort('project')}
                >
                  Project {sortBy === 'project' && (sortOrder === 'asc' ? '↑' : '↓')}
                </th>
                <th 
                  className="pb-3 text-xs font-medium text-gray-500 uppercase tracking-wide cursor-pointer hover:text-gray-700"
                  onClick={() => handleSort('dueDate')}
                >
                  Due Date {sortBy === 'dueDate' && (sortOrder === 'asc' ? '↑' : '↓')}
                </th>
                <th 
                  className="pb-3 text-xs font-medium text-gray-500 uppercase tracking-wide cursor-pointer hover:text-gray-700"
                  onClick={() => handleSort('priority')}
                >
                  Priority {sortBy === 'priority' && (sortOrder === 'asc' ? '↑' : '↓')}
                </th>
                <th 
                  className="pb-3 text-xs font-medium text-gray-500 uppercase tracking-wide cursor-pointer hover:text-gray-700"
                  onClick={() => handleSort('status')}
                >
                  Status {sortBy === 'status' && (sortOrder === 'asc' ? '↑' : '↓')}
                </th>
                <th className="pb-3 text-xs font-medium text-gray-500 uppercase tracking-wide">Actions</th>
              </tr>
            </thead>
            <tbody>
              <AnimatePresence>
                {filteredTasks.map((task, index) => (
                  <motion.tr
                    key={`${task.id}-${task.projectId}`}
                    initial={{opacity: 0, y: 10}}
                    animate={{opacity: 1, y: 0}}
                    exit={{opacity: 0, y: -10}}
                    transition={{delay: index * 0.02}}
                    className="border-b border-gray-100 hover:bg-gray-50"
                  >
                    {editingTask === task.id ? (
                      <>
                        <td colSpan="8" className="py-4">
                          <div className="space-y-3">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                              <div className="md:col-span-2">
                                <label className="block text-sm font-medium text-gray-700 mb-1">Task Title</label>
                                <input
                                  type="text"
                                  value={taskFormData.title}
                                  onChange={(e) => setTaskFormData(prev => ({...prev, title: e.target.value}))}
                                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                                />
                              </div>
                              <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Assignee</label>
                                <select
                                  value={taskFormData.assignee}
                                  onChange={(e) => setTaskFormData(prev => ({...prev, assignee: e.target.value}))}
                                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                                >
                                  <option value="">Select Assignee</option>
                                  {allUsers.map(user => (
                                    <option key={user.id} value={user.name}>{user.name}</option>
                                  ))}
                                </select>
                              </div>
                              <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Due Date</label>
                                <input
                                  type="date"
                                  value={taskFormData.dueDate}
                                  onChange={(e) => setTaskFormData(prev => ({...prev, dueDate: e.target.value}))}
                                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                                />
                              </div>
                              <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                                <select
                                  value={taskFormData.status}
                                  onChange={(e) => setTaskFormData(prev => ({...prev, status: e.target.value}))}
                                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                                >
                                  <option value="todo">To Do</option>
                                  <option value="in-progress">In Progress</option>
                                  <option value="review">Review</option>
                                  <option value="completed">Completed</option>
                                </select>
                              </div>
                              <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Priority</label>
                                <select
                                  value={taskFormData.priority}
                                  onChange={(e) => setTaskFormData(prev => ({...prev, priority: e.target.value}))}
                                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                                >
                                  <option value="low">Low Priority</option>
                                  <option value="medium">Medium Priority</option>
                                  <option value="high">High Priority</option>
                                </select>
                              </div>
                              <div className="md:col-span-2">
                                <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
                                <textarea
                                  value={taskFormData.notes}
                                  onChange={(e) => setTaskFormData(prev => ({...prev, notes: e.target.value}))}
                                  rows={2}
                                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                                  placeholder="Additional notes or context for this task"
                                />
                              </div>
                            </div>
                            <div className="flex gap-2">
                              <button
                                onClick={handleSaveTask}
                                className="flex items-center px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                              >
                                <SafeIcon icon={FiSave} className="w-4 h-4 mr-2" />
                                Save
                              </button>
                              <button
                                onClick={() => {setEditingTask(null); resetForm();}}
                                className="flex items-center px-4 py-2 bg-gray-300 text-gray-700 rounded-lg hover:bg-gray-400 transition-colors"
                              >
                                <SafeIcon icon={FiX} className="w-4 h-4 mr-2" />
                                Cancel
                              </button>
                            </div>
                          </div>
                        </td>
                      </>
                    ) : (
                      <>
                        <td className="py-3">
                          <button
                            onClick={() => handleTaskToggle(task)}
                            className={`${getTaskColor(task)} hover:scale-110 transition-transform`}
                          >
                            <SafeIcon icon={getTaskIcon(task)} className="w-5 h-5" />
                          </button>
                        </td>
                        <td className="py-3">
                          <div className={`font-medium ${task.status === 'completed' ? 'line-through text-gray-500' : 'text-gray-900'}`}>
                            {task.title}
                          </div>
                        </td>
                        <td className="py-3">
                          <div className="flex items-center text-gray-600">
                            <SafeIcon icon={FiUser} className="w-3 h-3 mr-1" />
                            {task.assignee}
                          </div>
                        </td>
                        <td className="py-3">
                          <span className="text-xs bg-purple-100 text-purple-800 px-2 py-1 rounded-full">
                            {task.projectTitle}
                          </span>
                        </td>
                        <td className="py-3">
                          {task.dueDate ? (
                            <div className={`flex items-center text-sm ${getTaskColor(task)}`}>
                              <SafeIcon icon={FiClock} className="w-3 h-3 mr-1" />
                              {format(parseISO(task.dueDate), 'MMM dd, yyyy')}
                            </div>
                          ) : (
                            <span className="text-sm text-gray-400">No date</span>
                          )}
                        </td>
                        <td className="py-3">
                          <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getPriorityColor(task.priority)}`}>
                            {task.priority}
                          </span>
                        </td>
                        <td className="py-3">
                          <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(task.status)}`}>
                            {task.status}
                          </span>
                        </td>
                        <td className="py-3">
                          <div className="flex items-center gap-2">
                            <button
                              onClick={() => handleEditTask(task)}
                              className="p-2 text-blue-600 hover:bg-blue-100 rounded-lg transition-colors"
                            >
                              <SafeIcon icon={FiEdit} className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => handleDeleteTask(task)}
                              className="p-2 text-red-600 hover:bg-red-100 rounded-lg transition-colors"
                            >
                              <SafeIcon icon={FiTrash2} className="w-4 h-4" />
                            </button>
                          </div>
                        </td>
                      </>
                    )}
                  </motion.tr>
                ))}
              </AnimatePresence>
            </tbody>
          </table>
        </div>

        {/* Empty State */}
        {filteredTasks.length === 0 && (
          <div className="text-center py-12 text-gray-500">
            <SafeIcon icon={FiCheckSquare} className="w-12 h-12 mx-auto mb-4 opacity-50" />
            <p>No tasks found for the selected filters</p>
            {data.projects.length === 0 && (
              <p className="text-sm mt-2">Create a project first to add tasks</p>
            )}
          </div>
        )}

        {/* Task Summary */}
        {allTasks.length > 0 && (
          <div className="mt-6 pt-6 border-t border-gray-200">
            <div className="text-center">
              <p className="text-sm text-gray-600">
                Showing {filteredTasks.length} of {allTasks.length} tasks
                {isMyTasksActive && ` (${tasksForStats.length} assigned to you)`}
              </p>
            </div>
          </div>
        )}
      </motion.div>

      {/* Create Project Modal */}
      <CreateProjectModal
        isOpen={showCreateProjectModal}
        onClose={() => setShowCreateProjectModal(false)}
        onSuccess={handleCreateProjectSuccess}
      />
    </>
  );
};

export default TasksView;